import { useConfirmation } from '@components/modal';
import { useDeleteVolumeMutation, type Volume } from '@generated/graphql';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';

type DeleteVolumeModalProps = {
  volume: Volume;
  onDelete?: VoidFunction;
};

export const useDeleteVolumeModal = ({ volume, onDelete }: DeleteVolumeModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const { showConfirmation } = useConfirmation();
  const [deleteVolumeMutation] = useDeleteVolumeMutation();

  const deleteVolume = createAsyncNotification(
    'Volume deleted successfully.',
    async () => {
      posthog.capture('agent-studio:user_delete-volume_submitted', {
        volumeId: volume.volumeId,
      });
      await deleteVolumeMutation({
        variables: { volumeId: volume.volumeId },
        update(cache) {
          cache.evict({ id: cache.identify(volume) });
          cache.gc();
        },
        onCompleted: () => {
          posthog.capture('agent-studio:user_delete-volume_completed', {
            volumeId: volume.volumeId,
          });
          onDelete?.();
        },
        onError: (error) => {
          posthog.capture('agent-studio:user_delete-volume_failed', {
            volumeId: volume.volumeId,
            error: (error as Error)?.message || 'An error occurred during deletion',
          });
          throw error;
        },
      });
    },
    {
      loadingMessage: 'Deleting volume...',
    },
  );

  const showModal = () =>
    showConfirmation({
      onAffirm: deleteVolume,
      message: `Are you sure you want to delete ${volume?.name || 'this volume'}?`,
    });

  return { showModal };
};
